import { useState } from "react"

import { Key, ContentCopy } from '@mui/icons-material'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert,
} from '@mui/material'

import { authCodesService, type User } from "../../api"

interface AuthCodeDialogProps {
  user: User
}

export function AuthCodeDialog({ user }: AuthCodeDialogProps) {
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedCode, setGeneratedCode] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleGenerateCode = async () => {
    try {
      setIsGenerating(true)
      setError(null)
      const result = await authCodesService.createAuthCode({ email: user.email })
      setGeneratedCode(result.code)
      setOpen(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate auth code')
      setOpen(true)
    } finally {
      setIsGenerating(false)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setSnackbarOpen(true)
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setSnackbarOpen(true)
    }
  }

  const handleClose = () => {
    setOpen(false)
    setGeneratedCode(null)
    setError(null)
  }

  return (
    <>
      <Button
        size="small"
        variant="contained"
        startIcon={<Key />}
        onClick={(e) => {
          e.stopPropagation()
          handleGenerateCode()
        }}
        disabled={isGenerating}
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          minWidth: 'auto',
          px: 1.5,
          py: 0.5,
          height: 28,
          backgroundColor: 'primary.main',
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
          '&:disabled': {
            backgroundColor: 'action.disabledBackground',
            color: 'action.disabled',
          },
        }}
      >
        {isGenerating ? 'Generating...' : 'Generate'}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Auth Code Generated
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            For: {user.email}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {generatedCode && (
            <Box
              sx={{
                bgcolor: 'grey.100',
                p: 3,
                borderRadius: 1,
                border: 1,
                borderColor: 'grey.300',
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'grey.200',
                },
              }}
              onClick={() => copyToClipboard(generatedCode)}
              title="Click to copy code"
            >
              <Typography
                component="code"
                variant="h4"
                fontFamily="monospace"
                fontWeight="bold"
                sx={{ userSelect: 'all' }}
              >
                {generatedCode}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => generatedCode && copyToClipboard(generatedCode)}
            startIcon={<ContentCopy />}
            variant="contained"
            disabled={!generatedCode}
          >
            Copy Code
          </Button>
          <Button onClick={handleClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success">
          Code copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  )
}
